import React, { useEffect, useState } from "react";
import { Box, Typography, Grid2 as Grid, IconButton } from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import AddIcon from "@mui/icons-material/Add";
import { useUserData } from "components/customHooks/userDataContext";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import { getPreChartDocument, getPreChartDocuments } from "apiHandler/actions";
import { useLocation } from "react-router-dom";

import DocumentSidebar from "./documentSidebar";
import DocumentViewerPanel from "./documentViewerPanel";

const DocumentsUI = () => {
  const [documents, setDocuments] = useState([]);
  const [selectedDocIndex, setSelectedDocIndex] = useState(null);
  const [documentUrl, setDocumentUrl] = useState(null);
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [loading, setLoading] = useState(true);

  const defaultLayoutPluginInstance = defaultLayoutPlugin();

  const location = useLocation();
  const rowData = location.state?.data;

  const userData = useUserData();
  const token = userData?.access_token;

  useEffect(() => {
    getDocuments();
  }, []);

  useEffect(() => {
    return () => {
      if (documentUrl?.url) {
        URL.revokeObjectURL(documentUrl.url);
      }
    };
  }, [documentUrl]);

  const getDocuments = async () => {
    setLoading(true);
    try {
      const payload = {
        patient_id: `${rowData?.patient_id || ""}`,
        encounter_id: `${rowData?.encounter_id || rowData?.id || ""}`,
        practice_id: userData?.selected_practice,
      };

      const res = await getPreChartDocuments(payload, token);
      const docs = Array.isArray(res) ? res : [];

      setDocuments(docs);

      if (docs.length > 0) {
        handleSelectDocument(0, docs[0]);
      } else {
        setSelectedDocIndex(null);
        setDocumentUrl(null);
      }
    } catch (error) {
      console.log("error: ", error);
      setDocuments([]);
      setSelectedDocIndex(null);
      setDocumentUrl(null);
    } finally {
      setLoading(false);
    }
  };

  const getDocumentFile = async (doc) => {
    if (!doc?.id) {
      setDocumentUrl(null);
      return;
    }

    setLoadingDoc(true);
    try {
      const payload = {
        type: "Documents",
        document_id: `${doc.id}`,
        practice_id: userData?.selected_practice,
      };

      const blob = await getPreChartDocument(payload, token);
      const mimeType = blob?.type || "application/octet-stream";
      const url = URL.createObjectURL(blob);

      setDocumentUrl((prev) => {
        if (prev?.url) URL.revokeObjectURL(prev.url);
        return { url, mimeType };
      });
    } catch (error) {
      console.error("Error loading document:", error);
      setDocumentUrl(null);
    } finally {
      setLoadingDoc(false);
    }
  };

  const handleSelectDocument = (idx, doc) => {
    setSelectedDocIndex(idx);
    setDocumentUrl(null);
    getDocumentFile(doc);
  };

  const selectedDoc =
    selectedDocIndex !== null ? documents[selectedDocIndex] : null;

  return (
    <Box p={2}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <DescriptionIcon sx={{ color: "#2aa198" }} />
              <Typography fontWeight={600} color="#2aa198">
                Documents
              </Typography>
            </Box>

            <IconButton
              sx={{
                border: "1px solid",
                borderColor: "grey.300",
                backgroundColor: "#fff",
                borderRadius: 2,
              }}
            >
              <AddIcon />
            </IconButton>
          </Box>

          <Box sx={{ display: "flex", gap: 2, height: "70vh" }}>
            <DocumentSidebar
              documents={documents}
              loading={loading}
              selectedDocIndex={selectedDocIndex}
              onSelectDocument={handleSelectDocument}
            />

            <DocumentViewerPanel
              selectedDoc={selectedDoc}
              documentUrl={documentUrl}
              loadingDoc={loadingDoc}
              defaultLayoutPluginInstance={defaultLayoutPluginInstance}
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DocumentsUI;
